import * as React from 'react'

const UNDEFINED = '@@UNDEFINED@@'
const BOOLEAN = '@@BOOLEAN@@-'
const NUMBER = '@@NUMBER@@-'

const STORAGE_KEY = '@@DS@@-'

/**
 * A simple implementation of Storage get|setItem backed by a Map.
 */
class MapStorage implements Pick<Storage, 'getItem' | 'setItem'> {
  private readonly _storage = new Map<string, string>()

  /** @inheritdoc */
  getItem(key: string): string | null {
    return this._storage.get(key) ?? null
  }

  /** @inheritdoc */
  setItem(key: string, value: string): void {
    this._storage.set(key, value)
  }
}

function getStateFromStorage<T extends string | boolean | number>(
  storage: Pick<Storage, 'getItem'>,
  storageID: string,
  defaultValue?: T
): T | undefined {
  const existingState = storage.getItem(storageID)
  if (existingState === null) {
    return defaultValue
  }
  if (existingState === UNDEFINED) {
    return undefined
  }
  if (existingState.startsWith(BOOLEAN)) {
    return (existingState.replace(BOOLEAN, '') === 'true') as T
  }
  if (existingState.startsWith(NUMBER)) {
    return Number(existingState.replace(NUMBER, '')) as T
  }
  return existingState as T
}

function saveStateToStorage<T extends string | boolean | number>(
  storage: Pick<Storage, 'setItem'>,
  storageID: string,
  state?: T
) {
  let newState: string
  if (state === undefined) {
    newState = UNDEFINED
  } else if (typeof state === 'number') {
    newState = `${NUMBER}${state}`
  } else if (typeof state === 'boolean') {
    newState = `${BOOLEAN}${state}`
  } else {
    newState = state as string
  }

  try {
    storage.setItem(storageID, newState)
  } catch (_err) {
    // The API `throw`s if Storage is full.
    // https://developer.mozilla.org/en-US/docs/Web/API/Storage/setItem
  }
}

/**
 * Make sure localStorage is available. This should throw without localStorage.
 * Modernizr approach to checking for localStorage: https://github.com/Modernizr/Modernizr/blob/master/feature-detects/storage/localstorage.js#L40
 * To disable localStorage: open -a Google\ Chrome --args --disable-local-storage
 */
function checkStorage(storage: Pick<Storage, 'getItem' | 'setItem'>) {
  const key = `${STORAGE_KEY}${Math.random()}`
  storage.setItem(key, '')
  ;(storage as Storage).removeItem(key)
}

/**
 * A useState hook which backs the state with storage (local or session).
 * Gracefully handles exceptions caused by lack of access to the underlying
 * Storage implementation or Storage being full.
 */

function useStorage<T extends string | boolean | number>(
  /** Storage (e.g., window.localStorage or window.sessionStorage) */
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  /**
   * The storage key
   */
  storageID: string,
  /**
   * The value that will be returned when storage doesn't contain data
   * for the specified storageID.
   *
   * It does not store the value in storage, you must use the setter to persist values.
   */
  defaultValue?: T
): [T | undefined, (value?: T | ((prevState?: T) => T)) => void] {
  const [state, setState] = React.useState<T | undefined>(() => {
    return getStateFromStorage(storage, storageID, defaultValue) as T
  })

  const wrappedSetter: (value?: T | ((prevState?: T) => T)) => void =
    React.useCallback(
      (value) => {
        setState((currentState) => {
          const nextState =
            typeof value === 'function' ? value(currentState) : value
          saveStateToStorage(storage, storageID, nextState)
          return nextState
        })
      },
      [storage, storageID]
    )

  return [state, wrappedSetter]
}

// Check whether window.{local,session}Storage are available.
// We do this lazily because otherwise unit testing becomes very difficult.
//
// window.{local,session}Storage may `throw` when accessed depending on a user's
// browser settings.
// See https://www.chromium.org/for-testers/bug-reporting-guidelines/uncaught-securityerror-failed-to-read-the-localstorage-property-from-window-access-is-denied-for-this-document/
//
// window.{local,session}Storage may `throw` when accessed in an Android WebView
// based on its configuration.
// See https://developer.android.com/reference/android/webkit/WebSettings#setDomStorageEnabled(boolean)
const [getSafeLocalStorage, getSafeSessionStorage, clearStorage] =
  (function () {
    let safeLocalStorage: Pick<Storage, 'getItem' | 'setItem'> | undefined
    let safeSessionStorage: Pick<Storage, 'getItem' | 'setItem'> | undefined

    const getLocal = () => {
      if (safeLocalStorage == null) {
        try {
          safeLocalStorage = window.localStorage
          checkStorage(safeLocalStorage)
        } catch (_err) {
          safeLocalStorage = new MapStorage()
        }
      }
      return safeLocalStorage
    }
    const getSession = () => {
      if (safeSessionStorage == null) {
        try {
          safeSessionStorage = window.sessionStorage
          checkStorage(safeSessionStorage)
        } catch (_err) {
          safeSessionStorage = new MapStorage()
        }
      }
      return safeSessionStorage
    }
    const clear = () => {
      safeLocalStorage = undefined
      safeSessionStorage = undefined
    }
    return [getLocal, getSession, clear]
  })()

/**
 * A useState hook which backs the state with local storage.
 */
export function useLocalStorage<T extends string | boolean | number>(
  /**
   * The local storage key
   */
  storageID: string,
  /**
   * The value that will be returned when local storage doesn't contain data
   * for the specified storageID.
   *
   * It does not store the value in local storage, you must use the setter to persist values.
   */
  defaultValue?: T
): [T | undefined, (value?: T | ((prevState?: T) => T)) => void] {
  return useStorage(getSafeLocalStorage(), storageID, defaultValue)
}

/**
 * A useState hook which backs the state with session storage.
 */
export function useSessionStorage<T extends string | boolean | number>(
  /**
   * The session storage key
   */
  storageID: string,
  /**
   * The value that will be returned when session storage doesn't contain data
   * for the specified storageID.
   *
   * It does not store the value in session storage, you must use the setter to persist values.
   */
  defaultValue?: T
): [T | undefined, (value?: T | ((prevState?: T) => T)) => void] {
  return useStorage(getSafeSessionStorage(), storageID, defaultValue)
}

export const clearStorage_OnlyForTesting = clearStorage
